import React, { useEffect, useMemo, useState } from "react";
import { api, apiErr, fileUrl } from "@/lib/api";
import { formatGs } from "@/lib/format";
import { Modal, Btn, Input } from "@/components/ui/kit";
import ImageSearchModal from "@/components/ImageSearchModal";
import { usePasteImage } from "@/lib/pasteImage";
import { ImageCropModal } from "@/components/ImageCropModal";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { Plus, Minus, Trash2, Search, Package, Layers } from "lucide-react";

const emptyCombo = { name: "", category: "Combos", price: 0, cost: 0, image_url: "", barcode: "", combo_items: [] };

export const ComboModal = ({ open, combo, onClose, onSaved }) => {
  const { location } = useAuth();
  const [form, setForm] = useState(emptyCombo);
  const [all, setAll] = useState([]);
  const [q, setQ] = useState("");
  const [imgSearch, setImgSearch] = useState(false);
  const [cropSrc, setCropSrc] = useState(null);
  const setImg = (url) => { setForm((f) => ({ ...f, image_url: url })); setCropSrc(url); };
  usePasteImage(open && !imgSearch && !cropSrc, setImg);

  useEffect(() => {
    if (!open) return;
    setForm(combo ? { ...emptyCombo, ...combo } : emptyCombo);
    setQ("");
    api.get("/products", { params: { location } }).then((r) => setAll(r.data.filter((p) => !p.is_combo))).catch(() => {});
  }, [open, combo]);

  const byId = useMemo(() => Object.fromEntries(all.map((p) => [p.id, p])), [all]);
  const matches = q.trim() ? all.filter((p) => p.name.toLowerCase().includes(q.toLowerCase()) || p.barcode === q.trim()).slice(0, 6) : [];
  const items = form.combo_items;
  const setItems = (ci) => setForm((f) => ({ ...f, combo_items: ci }));
  const add = (p) => { setItems(items.some((i) => i.product_id === p.id) ? items.map((i) => i.product_id === p.id ? { ...i, quantity: i.quantity + 1 } : i) : [...items, { product_id: p.id, quantity: 1 }]); setQ(""); };
  const qty = (id, d) => setItems(items.map((i) => i.product_id === id ? { ...i, quantity: Math.max(1, i.quantity + d) } : i));
  const regular = items.reduce((s, i) => s + (byId[i.product_id]?.price || 0) * i.quantity, 0);
  const cost = items.reduce((s, i) => s + (byId[i.product_id]?.cost || 0) * i.quantity, 0);

  const save = async () => {
    if (items.length < 2 && !(items.length === 1 && items[0].quantity >= 2)) { toast.error("Elegí 2 o más productos"); return; }
    if (!form.name.trim() || !Number(form.price)) { toast.error("Completá nombre y precio del combo"); return; }
    const body = { ...form, price: Number(form.price), cost, is_combo: true, min_stock: 0, unit: "combo", stock_sabornauta: 0, stock_compunauta: 0, is_active: true };
    try {
      if (form.id) await api.put(`/products/${form.id}`, body); else await api.post("/products", body, { params: { location } });
      toast.success("Combo guardado"); onSaved();
    } catch (e) { toast.error(apiErr(e)); }
  };

  return (
    <Modal open={open} onClose={onClose} title={form.id ? "Editar combo" : "Nuevo combo"} wide top>
      <div className="space-y-4" data-testid="combo-modal">
        <div className="flex gap-4">
          <button type="button" onClick={() => setImgSearch(true)} data-testid="combo-image-btn" className="w-24 h-24 rounded-lg bg-secondary overflow-hidden shrink-0 border border-border hover:border-primary">
            {form.image_url ? <img src={fileUrl(form.image_url)} alt="" className="w-full h-full object-cover" /> : <Layers className="w-8 h-8 m-8 text-muted-foreground" />}
          </button>
          <div className="flex-1 grid grid-cols-2 gap-3">
            <div className="col-span-2"><label className="text-sm font-medium mb-1 block">Nombre del combo</label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="combo-name" placeholder="Ej: Café + Medialuna" /></div>
            <div><label className="text-sm font-medium mb-1 block">Precio único (Gs.)</label><Input type="number" value={form.price || ""} onChange={(e) => setForm({ ...form, price: e.target.value })} data-testid="combo-price" /></div>
            <div><label className="text-sm font-medium mb-1 block">Código de barras</label><Input value={form.barcode || ""} onChange={(e) => setForm({ ...form, barcode: e.target.value })} data-testid="combo-barcode" /></div>
          </div>
        </div>
        <div className="relative">
          <Search className="w-4 h-4 absolute left-3 top-3 text-muted-foreground" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Buscar producto o escanear código para agregar…" className="pl-9" data-testid="combo-search" />
          {matches.length > 0 && (
            <div className="absolute z-10 left-0 right-0 mt-1 card-surface p-1 shadow-xl">
              {matches.map((p) => (
                <button key={p.id} onClick={() => add(p)} data-testid={`combo-add-${p.id}`} className="w-full flex items-center justify-between px-3 py-2 rounded-md hover:bg-accent text-sm">
                  <span className="flex items-center gap-2"><Package className="w-4 h-4 text-muted-foreground" /> {p.name}</span>
                  <span className="font-data text-muted-foreground">{formatGs(p.price)}</span>
                </button>
              ))}
            </div>
          )}
        </div>
        <div className="space-y-2" data-testid="combo-items">
          {items.length === 0 && <p className="text-sm text-muted-foreground text-center py-4">Agregá 2 o más productos al combo</p>}
          {items.map((i) => (
            <div key={i.product_id} className="flex items-center gap-2 bg-secondary rounded-lg p-2">
              <span className="flex-1 text-sm font-medium truncate">{byId[i.product_id]?.name || "…"}</span>
              <button onClick={() => qty(i.product_id, -1)} data-testid={`combo-dec-${i.product_id}`} className="w-6 h-6 rounded bg-background flex items-center justify-center"><Minus className="w-3 h-3" /></button>
              <span className="w-6 text-center text-sm font-semibold">{i.quantity}</span>
              <button onClick={() => qty(i.product_id, 1)} data-testid={`combo-inc-${i.product_id}`} className="w-6 h-6 rounded bg-background flex items-center justify-center"><Plus className="w-3 h-3" /></button>
              <button onClick={() => setItems(items.filter((x) => x.product_id !== i.product_id))} data-testid={`combo-remove-${i.product_id}`} className="text-muted-foreground hover:text-destructive"><Trash2 className="w-4 h-4" /></button>
            </div>
          ))}
        </div>
        {items.length > 0 && (
          <div className="text-xs text-muted-foreground flex justify-between" data-testid="combo-regular">
            <span>Precio por separado: <b className="font-data">{formatGs(regular)}</b></span>
            {Number(form.price) > 0 && <span>Ahorro cliente: <b className="font-data text-emerald-500">{formatGs(regular - Number(form.price))}</b></span>}
          </div>
        )}
        <div className="flex gap-2">
          <Btn variant="outline" onClick={onClose} className="flex-1">Cancelar</Btn>
          <Btn onClick={save} data-testid="combo-save" className="flex-1">Guardar combo</Btn>
        </div>
      </div>
      <ImageSearchModal open={imgSearch} onClose={() => setImgSearch(false)} defaultQuery={form.name} barcode={form.barcode || ""} onSelect={setImg} />
      <ImageCropModal src={cropSrc} onClose={() => setCropSrc(null)} onDone={(url) => { setForm((f) => ({ ...f, image_url: url })); setCropSrc(null); }} />
    </Modal>
  );
};
